import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxTextBox} from 'fmlx-common-ui';

function RatioTextBox({style, innerRef, value, placeholder, onChange, onBlur, maxValue}) {
  const [localValue, setLocalValue] = useState(value);
  const [changeCursorPositionBeforeSlash, setChangeCursorPositionBeforeSlash] = useState(false);

  const textBoxRef = useRef(null);

  const getLocalValueToString = () => `${localValue[0]}/${localValue[1]}`;

  // change the cursor position to just before the slash
  useEffect(() => {
    if (changeCursorPositionBeforeSlash) {
      textBoxRef.current.selectionStart = getLocalValueToString().length - 1;
      textBoxRef.current.selectionEnd = getLocalValueToString().length - 1;
      setChangeCursorPositionBeforeSlash(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeCursorPositionBeforeSlash]);

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isValueValid = (val) => {
    const regex = /[0-9]|\//g; // accepts numbers and forward slash only
    const regexForwardSlash = /\//g; // count the number of forward slashes;

    const matches = val.match(regex);
    const forwardSlashes = val.match(regexForwardSlash);

    const errorList = {
      noForwardSlash: !forwardSlashes,
      moreThanOneForwardSlash: forwardSlashes?.length > 1,
      illegalCharacter: val !== '' && matches?.length !== val?.length && val !== '/', // if the only word left isnt forward slash and if the newly inputted word is invalid
    };

    return errorList;
  };

  const processChanges = (val) => {
    const errorList = isValueValid(val);
    const isValid = !Object.values(errorList).find((err) => err);
    let modifiedVal = val;

    if (!isValid) {
      if (errorList.noForwardSlash && !errorList.illegalCharacter) {
        modifiedVal = `${modifiedVal}/`;
        setChangeCursorPositionBeforeSlash(true);
      } else return;
    }

    const newValue = modifiedVal.split('/');
    const [numerator, denumerator] = newValue;

    if (numerator > maxValue || denumerator > maxValue) return;

    if (onChange) onChange(newValue);
    setLocalValue(newValue);
  };

  const handleOnBlur = () => {
    let [numerator, denumerator] = localValue;
    if (numerator === '') {
      numerator = 0;
    }
    if (denumerator === '') {
      denumerator = 0;
    }

    const newValue = [Number(numerator), Number(denumerator)];
    setLocalValue(newValue);
    onBlur(newValue);
  };

  return (
    <span className="ratio-text-box" ref={innerRef} style={style}>
      <FmlxTextBox
        ref={textBoxRef}
        placeholder={placeholder}
        value={getLocalValueToString()}
        onChange={(val) => processChanges(val.value, true)}
        onBlur={handleOnBlur}
        onEnterKeyPressed={handleOnBlur}
      />
    </span>
  );
}

RatioTextBox.propTypes = {
  style: PropTypes.object,
  innerRef: PropTypes.object,
  value: PropTypes.array,
  maxValue: PropTypes.number,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

RatioTextBox.defaultProps = {
  style: {},
  innerRef: undefined,
  maxValue: 9999,
  value: [0, 0],
  placeholder: '1/10',
  onChange: null,
  onBlur: null,
};

export default RatioTextBox;
